import { useMemo, useCallback, useRef } from "react";
import { toast } from "react-hot-toast";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { getAccessToken } from "auth/tokenStorage";
import { useTranslation } from "./useTranslation";

export interface AgentImportWebSocketMessage {
  task_id?: string;
  status?: string;
  message?: string;
  agent_id?: string;
  error?: string;
  [key: string]: any;
}

interface UseAgentImportWebSocketOptions {
  taskId?: string | null;
  onStatusUpdate?: (message: AgentImportWebSocketMessage) => void;
  enabled?: boolean;
}

/**
 * Build WebSocket base URL
 */
const getWebSocketBaseUrl = (): string => {
  const apiUrl = window.appConfig.apiUrl;
  let wsUrl = apiUrl.replace(/^http:/, "ws:").replace(/^https:/, "wss:");
  return wsUrl.replace(/\/api\/?$/, "");
};

export const useAgentImportWebSocket = ({
  taskId,
  onStatusUpdate,
  enabled = true,
}: UseAgentImportWebSocketOptions) => {
  const { t } = useTranslation();
  const shouldCloseRef = useRef(false);
  const getToastId = (id: string) => `import-${id}`;

  // Build WebSocket URL
  const socketUrl = useMemo(() => {
    if (!taskId) {
      return null;
    }
    const baseUrl = getWebSocketBaseUrl();
    return `${baseUrl}/ws/agent-import/${taskId}`;
  }, [taskId]);

  // Get token for query params
  const token = useMemo(() => getAccessToken(), []);

  // Determine if we should connect
  const shouldConnect = enabled && !!token && !!taskId;

  // Status handlers for toast notifications
  const statusHandlers = useMemo(
    () => {
      const toastId = taskId ? getToastId(taskId) : "import";
      return {
        pending: {
          toast: (data: AgentImportWebSocketMessage) =>
            toast.loading(data.message || t("agents.import.pending") || "Import pending...", { id: toastId }),
          shouldClose: false,
        },
        processing: {
          toast: (data: AgentImportWebSocketMessage) =>
            toast.loading(data.message || t("agents.import.processing") || "Processing import...", { id: toastId }),
          shouldClose: false,
        },
        completed: {
          toast: (data: AgentImportWebSocketMessage) =>
            toast.success(
              data.message || t("agents.import.success") || "Agent imported successfully!",
              { id: toastId }
            ),
          shouldClose: true,
        },
        failed: {
          toast: (data: AgentImportWebSocketMessage) =>
            toast.error(
              data.error || data.message || t("agents.import.failed") || "Agent import failed",
              { id: toastId }
            ),
          shouldClose: true,
        },
      };
    },
    [taskId, t]
  );

  // Handle incoming messages using onMessage callback (more efficient than useEffect)
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      try {
        const data: AgentImportWebSocketMessage = JSON.parse(event.data);
        console.log("Received WebSocket message:", data);

        // Handle status updates with custom handlers
        if (data.status && data.status in statusHandlers) {
          const handler = statusHandlers[data.status as keyof typeof statusHandlers];
          handler.toast(data);
          if (handler.shouldClose) {
            shouldCloseRef.current = true;
          }
        }

        // Handle generic messages without status
        if (!data.status && data.message && taskId) {
          toast.loading(data.message, { id: getToastId(taskId) });
        }

        // Call status update callback
        if (data.status && onStatusUpdate) {
          onStatusUpdate(data);
        }
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    },
    [onStatusUpdate, statusHandlers, taskId]
  );

  // Handle connection open
  const handleOpen = useCallback(() => {
    console.log("WebSocket connected:", taskId);
    shouldCloseRef.current = false;
  }, [taskId]);

  // Handle connection errors
  const handleError = useCallback(
    (event: Event) => {
      console.error("WebSocket error:", event);
      if (taskId && !token) {
        toast.error("Authentication required for import status updates");
      }
    },
    [taskId, token]
  );

  // Handle reconnection stop (max attempts reached)
  const handleReconnectStop = useCallback(
    (numAttempts: number) => {
      if (taskId) {
        toast.error(t("agents.import.connectionFailed") || "Failed to connect to import status updates", {
          id: getToastId(taskId),
        });
      }
    },
    [taskId, t]
  );

  // Determine if we should reconnect
  const shouldReconnect = useCallback(
    (closeEvent: CloseEvent | null) => {
      // Don't reconnect if shouldClose was set (e.g., task completed)
      if (shouldCloseRef.current) {
        return false;
      }

      // Don't reconnect if it was a normal closure
      if (closeEvent?.code === 1000) {
        return false;
      }

      // Don't reconnect if disabled
      if (!enabled) {
        return false;
      }

      return true;
    },
    [enabled]
  );

  // Use react-use-websocket with library features
  const { getWebSocket, readyState } = useWebSocket<AgentImportWebSocketMessage>(
    socketUrl,
    {
      onOpen: handleOpen,
      onMessage: handleMessage,
      onError: handleError,
      onReconnectStop: handleReconnectStop,
      shouldReconnect,
      reconnectAttempts: 5,
      reconnectInterval: 3000,
      queryParams: token ? { token } : undefined,
    },
    shouldConnect
  );

  // Return disconnect function
  const disconnect = useCallback(() => {
    shouldCloseRef.current = true;
    const ws = getWebSocket();
    if (ws && readyState === ReadyState.OPEN) {
      ws.close();
    }
  }, [getWebSocket, readyState]);

  return { disconnect };
};

